<template>
  <div
    class="dropdown"
    :class="{
      'dropdown--disabled': disabled,
      'dropdown--compact': compact,
    }"
    @keydown.enter.prevent="toggle"
    @keydown.space="preventSpace ? toggle() : ''"
    @keydown.arrow-up.prevent="optionTrayKeyHandler"
    @keydown.arrow-down.prevent="optionTrayKeyHandler"
    @keydown.enter="keydownHandler"
    @blur="onBlur"
    tabindex="0"
    ref="dropdown"
  >
    <div
      class="dropdown_button"
      :class="{
        'dropdown_button--opened': isOpen,
        'dropdown_button--disabled': disabled,
        'dropdown_button--compact': compact,
        'dropdown_button--invalid':
          enableExternalValidation && externalValidationError,
        'dropdown_button--font-weight-regular': fontWeightRegular,
      }"
      @click="toggle"
    >
      <div
        class="dropdown_button__value"
        :class="{
          'dropdown_button__value--thick': isOptionSelected,
          'dropdown_button__value--disabled': disabled,
          'dropdown_button__value--font-weight-regular': fontWeightRegular,
          'dropdown_button__value--font-size-regular': fontSizeRegular,
        }"
      >
        {{ displayValue }}
        <span
          class="required_asterisk"
          v-if="showRequiredAsterisk && !isOptionSelected"
          >*</span
        >
      </div>
      <font-awesome-icon
        class="dropdown_button__icon"
        :icon="['fal', dropdownButtonIcon]"
      />
    </div>
    <OptionTray
      class="textinput_autocomplete__tray"
      v-if="isOpen"
      :parentValue="inputValue"
      :options="options"
      :placeholder="placeholder"
      :labelNoResults="labelNoResults"
      :searchAble="searchAble"
      :searchbarPlaceholder="searchbarPlaceholder"
      ref="optionTray"
      @requestClose="close()"
      @setOption="setOption"
      :visibleItems="visibleItems"
    />
    <ValidationMessage
      v-if="!disableErrorMessageBlock"
      :displayInvalid="displayInvalidMessage"
      :validation-message="externalValidationErrorMessage || validationMessage"
    />
  </div>
</template>

<script>
import BaseComponent from "../../BaseComponent";
import InputBase from "./mixins/InputBase";
import OptionTray from "../OptionTray.vue";

/**
 * @group Inputs
 * Dropdown a.k.a. Selectbox,
 * user can click this open to select an option out of a list that is preset.
 */
export default {
  name: "Dropdown",
  mixins: [BaseComponent, InputBase],
  components: {
    OptionTray,
  },
  props: {
    searchAble: {
      type: Boolean,
      default: false,
    },
    // Option List
    options: {
      type: Array,
      required: true,
    },
    // Grey text that is shown when no option is selected
    placeholder: {
      type: String,
      required: true,
    },
    required: {
      type: Boolean,
      default: false,
    },
    validationMessage: {
      type: String,
      default: "Please select an option",
    },
    compact: {
      type: Boolean,
      default: false,
    },
    // Flag to allow control of error state. If false, component will apply internal validation logic.
    enableExternalValidation: {
      type: Boolean,
      default: false,
    },
    // Flag to tell this component should show error state.
    externalValidationError: {
      type: Boolean,
    },
    // Error message that gets shown when externalValidationError is true.
    externalValidationErrorMessage: {
      type: String,
    },
    // Flag to disable the validation error message text entirely while preserving other error related styling.
    disableErrorMessageBlock: {
      type: Boolean,
      default: false,
    },
    // set the font weight to be regular
    fontWeightRegular: {
      type: Boolean,
      default: false,
    },
    // set the font size to be regular (1rem)
    fontSizeRegular: {
      type: Boolean,
      default: false,
    },
    showRequiredAsterisk: {
      type: Boolean,
      default: false,
    },
    visibleItems: {
      type: Number,
      required: false,
    },
    /**
     * Placeholder text to be displayed in the searchbar if present
     */
    searchbarPlaceholder: {
      type: String,
    },
    labelNoResults: {
      type: String,
      default: "No result",
    },
    preventSpace: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
    };
  },
  computed: {
    displayInvalidMessage() {
      if (this.enableExternalValidation) {
        return (
          this.externalValidationErrorMessage.length !== 0 &&
          this.externalValidationError
        );
      }

      return this.isShowingValidationMessage && !this.isValid;
    },
    displayValue() {
      let returnDisplayValue = this.placeholder;
      if (this.inputValue) {
        this.options.forEach((option) => {
          if (option.value === this.inputValue) {
            returnDisplayValue = option.displayValue;
          }
        });
      }
      return returnDisplayValue;
    },
    dropdownButtonIcon() {
      let dropdownButtonIcon = "chevron-down";
      if (this.isOpen) dropdownButtonIcon = "chevron-up";
      return dropdownButtonIcon;
    },
    isOptionSelected() {
      return this.displayValue !== this.placeholder;
    },
    isValid() {
      return !this.required || this.isOptionSelected;
    },
  },
  methods: {
    setOption({ value }) {
      if (this.value === this.placeholder) {
        this.clear();
      } else {
        this.inputValue = value;
      }
    },
    toggle() {
      if (this.disabled) return;
      // eslint-disable-next-line no-unused-expressions
      this.isOpen ? this.close() : this.open();
    },
    open() {
      this.isOpen = true;
    },
    close() {
      this.isOpen = false;
      this.$refs.dropdown.blur();
    },
    clear() {
      this.inputValue = "";
      this.close();
    },
    onBlur(event) {
      if (
        this.$refs.optionTray !== undefined &&
        !this.$refs.optionTray.$el.contains(event.relatedTarget)
      ) {
        this.close();
      }
    },
    optionTrayKeyHandler(event) {
      if (this.disabled) return;
      if (!this.$refs.optionTray) {
        this.isOpen = true;
        return;
      }
      this.$refs.optionTray.keydownHandler(event.key);
    },
    keydownHandler(event) {
      if (this.disabled) return;
      if (!this.$refs.optionTray) return;
      if (!this.isOpen) return;
      event.preventDefault();
      this.$refs.optionTray.keydownHandler(event.key);
    },
  },
};
</script>

<style lang="scss">
.using-mouse {
  .dropdown_button {
    outline: 0px !important;
  }
}
</style>
<style scoped lang="scss">
@import "../../../style_variables/style_variables.scss";
$dropdown_padding: 14px;

.dropdown {
  position: relative;
  max-width: $input_max_width;
  outline: none !important;
  &:focus {
    .dropdown_button {
      outline: 5px auto rgba(0, 150, 255, 1);
    }
  }
  &.dropdown--disabled {
    color: $grey_mischka;
  }
  &.dropdown--compact {
    display: inline-block;
  }
}

.dropdown_button {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 56px;
  background-color: $white;
  border: 1px solid $grey_french;
  border-radius: 4px;
  padding: $dropdown_padding;

  &:hover {
    cursor: pointer;
    border: 1px solid $grey_bombay;
  }
  &.dropdown_button--disabled {
    background-color: $grey_bg;
    border: 1px solid $grey_mischka;
    &:hover {
      cursor: default;
      border: 1px solid $grey_mischka;
    }
  }
  &.dropdown_button--compact {
    min-height: 0;
    max-height: 38px;
  }
  &.dropdown_button--invalid {
    border: 1px solid $red;
  }
  &.dropdown_button--opened {
    color: $grey_mineshaft;
    border: 1px solid $grey_french;
    outline: none !important;
  }
  &.dropdown_button--font-weight-regular {
    font-weight: 400;
  }
}
.dropdown_button__value {
  @extend %button_typography_style;
  color: $grey_french;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.dropdown_button__value--disabled {
    color: $grey_mischka;
  }

  &.dropdown_button__value--font-weight-regular {
    font-weight: 400;
  }

  &.dropdown_button__value--font-size-regular {
    font-size: 1rem;
  }
}
.dropdown_button__value--thick {
  color: $grey_mineshaft;
}
.dropdown_button__icon {
  margin-left: 15px;
}
.required_asterisk {
  color: $red;
}
</style>
